import { mdiAlertOutline, mdiChevronRight, mdiTrayArrowDown } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";
import { formatNumber } from "helpers/formatNumber";
import { useMemo, useRef } from "react";
import type { IPendingScheduleBucket } from "store/home/types";
import { useLocalStorage } from "usehooks-ts";
import downloadPendingScheduleCsv, { buildPendingScheduleRows } from "../pendingScheduleCsv";
import styles from "./PendingSchedulePanel.module.scss";

const PAST_DUE_BUCKET = -1;
const TODAY_BUCKET = 0;
const LAST_WEEK_ONE_BUCKET = 5;
const PANEL_OPEN_STORAGE_KEY = "haulpay.wallet.pendingScheduleOpen";

interface IProps {
  schedule: IPendingScheduleBucket[];
  asOf?: string;
}

const formatAsOf = (asOf?: string) => {
  if (!asOf) return null;
  const value = new Date(asOf);
  if (Number.isNaN(value.getTime())) return null;
  return value.toLocaleString(undefined, {
    timeZone: "UTC",
    timeZoneName: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
};

const formatDayHint = (isoDate?: string | null) => {
  if (!isoDate) return null;
  const value = new Date(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(value.getTime())) return null;
  return value.toLocaleDateString(undefined, {
    timeZone: "UTC",
    weekday: "short",
    month: "short",
    day: "numeric"
  });
};

const compactCurrency = (amount: number) => {
  const magnitude = Math.abs(amount);
  const sign = amount < 0 ? "-" : "";
  if (magnitude >= 1e6) return `${sign}$${(magnitude / 1e6).toFixed(2)}M`;
  if (magnitude >= 1e3) return `${sign}$${Math.round(magnitude / 1e3)}K`;
  return `${sign}$${magnitude.toFixed(0)}`;
};

export default function PendingSchedulePanel({ schedule, asOf }: Readonly<IProps>) {
  const [isOpen, setIsOpen] = useLocalStorage(PANEL_OPEN_STORAGE_KEY, false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const rows = useMemo(() => buildPendingScheduleRows(schedule), [schedule]);

  if (!rows.some((row) => row.amountCents !== 0)) return null;

  const amountFor = (bucket: number) =>
    (rows.find((row) => row.bucket === bucket)?.amountCents ?? 0) / 100;
  const pastDueAmount = amountFor(PAST_DUE_BUCKET);
  const dueTodayAmount = amountFor(TODAY_BUCKET);
  const totalCents = rows[rows.length - 1].runningTotalCents;
  const totalCount = rows.reduce((count, row) => count + row.count, 0);
  const maxAmountCents = Math.max(...rows.map((row) => row.amountCents), 1);
  const asOfLabel = formatAsOf(asOf);
  const distribution = rows.filter((row) => row.sharePct > 0);

  const collapse = () => {
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <div className="mt-4 overflow-hidden border rounded-base border-border/15 bg-accent">
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={isOpen}
        aria-controls="pending-schedule-detail"
        onClick={() => setIsOpen(!isOpen)}
        className={clsx(
          "flex items-center w-full gap-3.5 px-4 py-2.5 text-sm text-left min-h-[44px] hover:bg-card",
          styles.trigger
        )}>
        <Icon
          path={mdiChevronRight}
          size={0.7}
          className={clsx(
            "shrink-0 transition-transform",
            isOpen ? "rotate-90 text-primary" : "text-subtitle"
          )}
        />

        <span className="font-semibold shrink-0">Pending balance by due date</span>

        <span className={clsx("flex flex-1 h-2 gap-0.5 min-w-[60px]", styles.distributionStrip)}>
          {distribution.map((row, index) => (
            <i
              key={row.bucket}
              title={`${row.label} — ${formatNumber(row.amountCents / 100, 2)}`}
              style={{
                flex: row.sharePct,
                opacity:
                  row.bucket === PAST_DUE_BUCKET ? 1 : 1 - (index / distribution.length) * 0.55
              }}
              className={clsx(
                "block min-w-[2px] rounded-sm",
                row.bucket === PAST_DUE_BUCKET ? "bg-declinedBorder" : "bg-primary"
              )}
            />
          ))}
        </span>

        <span className="text-xs shrink-0 text-subtitle">
          <span className="font-semibold text-text">{compactCurrency(dueTodayAmount)}</span> due
          today ·{" "}
          <span className="font-semibold text-declinedBorder">
            {compactCurrency(pastDueAmount)} past due
          </span>
        </span>
      </button>

      {isOpen && (
        <div id="pending-schedule-detail" className="px-4 pt-3 border-t border-border/15 bg-card">
          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 pb-3 text-xs text-subtitle">
            <span>Not final — recalculated whenever a funding request is approved.</span>
            {asOfLabel && (
              <span className="font-mono text-[0.6875rem] whitespace-nowrap">
                As of {asOfLabel}
              </span>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className={clsx("w-full text-sm", styles.table)}>
              <thead>
                <tr className="font-mono text-[0.6875rem] uppercase tracking-[0.09em] text-subtitle">
                  <th className="py-2 pr-3 font-normal text-left">Due</th>
                  <th className="py-2 pr-3 font-normal text-right">Amount</th>
                  <th className={clsx("py-2 pr-3 font-normal text-right", styles.invoicesCell)}>
                    Invoices
                  </th>
                  <th className={clsx("py-2 pr-3 font-normal text-right", styles.shareCell)}>
                    Share
                  </th>
                  <th className={clsx("py-2 pr-3 w-[22%]", styles.distributionCell)}>
                    <span className="sr-only">Distribution</span>
                  </th>
                  <th className="py-2 font-normal text-right">Running total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const isPastDue = row.bucket === PAST_DUE_BUCKET;
                  const isWeekOne = row.bucket > TODAY_BUCKET && row.bucket <= LAST_WEEK_ONE_BUCKET;
                  const isGrouped = row.bucket > LAST_WEEK_ONE_BUCKET;
                  const rolledUpWeekend =
                    isWeekOne && row.window_start !== null && row.window_start !== row.window_end;

                  return (
                    <tr
                      key={row.bucket}
                      className={clsx(
                        "border-t border-border/10",
                        row.bucket === LAST_WEEK_ONE_BUCKET && styles.weekOneEnd
                      )}>
                      <td className={clsx("py-2 pr-3", isGrouped && "font-semibold")}>
                        {isPastDue ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold border rounded-full border-current text-declinedBorder bg-declinedBorder/15">
                            <Icon path={mdiAlertOutline} size={0.55} className="shrink-0" />
                            {row.label}
                          </span>
                        ) : (
                          <>
                            {row.label}
                            {row.bucket === TODAY_BUCKET && (
                              <span
                                className={clsx(
                                  "ml-1.5 font-mono text-[0.625rem] text-subtitle",
                                  styles.dayHint
                                )}>
                                {formatDayHint(row.window_start)}
                              </span>
                            )}
                            {rolledUpWeekend && (
                              <span className="block font-mono text-[0.625rem] uppercase tracking-[0.05em] text-subtitle">
                                incl. Sat–Sun
                              </span>
                            )}
                          </>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-right">
                        {formatNumber(row.amountCents / 100, 2)}
                      </td>
                      <td className={clsx("py-2 pr-3 text-right", styles.invoicesCell)}>
                        {row.count}
                      </td>
                      <td className={clsx("py-2 pr-3 text-right", styles.shareCell)}>
                        {row.sharePct.toFixed(1)}%
                      </td>
                      <td className={clsx("py-2 pr-3", styles.distributionCell)}>
                        <span
                          style={{ width: `${(row.amountCents / maxAmountCents) * 100}%` }}
                          className={clsx(
                            "block h-1.5 min-w-[3px] rounded-sm",
                            isPastDue ? "bg-declinedBorder" : "bg-primary",
                            isGrouped && "opacity-75"
                          )}
                        />
                      </td>
                      <td className="py-2 font-medium text-right">
                        {formatNumber(row.runningTotalCents / 100, 2)}
                      </td>
                    </tr>
                  );
                })}
                <tr className="font-semibold border-t border-border/25">
                  <td className="py-2 pr-3">Total pending</td>
                  <td className="py-2 pr-3 text-right">{formatNumber(totalCents / 100, 2)}</td>
                  <td className={clsx("py-2 pr-3 text-right", styles.invoicesCell)}>
                    {totalCount}
                  </td>
                  <td className={clsx("py-2 pr-3 text-right", styles.shareCell)}>100%</td>
                  <td className={clsx("py-2 pr-3", styles.distributionCell)} />
                  <td className="py-2 text-right" />
                </tr>
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center py-3 text-xs gap-x-4 gap-y-2 text-subtitle">
            <span>
              Week one is one row per business day — weekends roll into Monday. Later windows follow
              your payout-day tiers.
            </span>
            <button
              type="button"
              onClick={() => downloadPendingScheduleCsv(schedule, asOf)}
              className="flex items-center gap-1 font-semibold underline text-primary underline-offset-2">
              <Icon path={mdiTrayArrowDown} size={0.6} />
              Download CSV
            </button>
            <button
              type="button"
              onClick={collapse}
              className="font-semibold underline text-primary underline-offset-2">
              Collapse
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
